import { useEffect, useState } from "react";
import { Loader2, ImageIcon, Check } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "./ImageUpload";

export default function HeroAdmin({ api }) {
  const [data, setData] = useState(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/admin/featured-hero");
    setData(r.data);
    setUrl(r.data?.featured?.image_url || r.data?.recommended_image_url || "");
  };
  useEffect(() => { load().catch(() => toast.error("Couldn't load the hero settings.")); /* eslint-disable-next-line */ }, []);

  const apply = async (imageUrl) => {
    setBusy(true);
    try {
      await api.post("/admin/featured-hero", { image_url: imageUrl || null });
      toast.success("Homepage hero image updated.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the hero image.");
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return <div className="flex items-center gap-2 text-[#6E6E6E]" data-testid="hero-admin-loading"><Loader2 className="animate-spin" size={16} /> Loading…</div>;
  }

  const recommended = data.recommended_image_url;
  const current = data.featured?.image_url;

  return (
    <div className="max-w-3xl" data-testid="hero-admin">
      <div className="flex items-center gap-2">
        <ImageIcon size={18} className="text-[#2D2D2D]" />
        <h2 className="font-head font-semibold uppercase text-xl text-[#1E1E1E]">Homepage Hero Image</h2>
      </div>
      <p className="mt-2 text-sm text-[#6E6E6E]">
        The big photo on the homepage is your <strong>featured article's</strong> image
        {data.featured?.title ? <> — currently “{data.featured.title}”.</> : "."} Update it here in one click.
      </p>

      {!data.featured && (
        <p className="mt-4 text-sm text-[#B4443A]" data-testid="hero-no-featured">No featured article is set, so there's no homepage hero to update yet.</p>
      )}

      {data.featured && (
        <>
          <div className="mt-6">
            <span className="kicker text-[#6E6E6E]">Current hero</span>
            <div className="mt-2 aspect-[16/9] w-full max-w-md overflow-hidden border-2 border-[#D3CFC0] bg-[#E7E3D9]">
              {current ? (
                <img src={current} alt="Current homepage hero" className="w-full h-full object-cover" data-testid="hero-current-preview" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#6E6E6E] text-sm">No image set</div>
              )}
            </div>
          </div>

          <div className="mt-8 border-2 border-[#DB9130]/50 bg-[#F3F1EC] p-4">
            <span className="kicker text-[#DB9130]">Recommended</span>
            <p className="mt-1 text-sm text-[#1E1E1E]">The on-brand coach &amp; youth-players photo.</p>
            <div className="mt-3 flex items-center gap-4">
              <img src={recommended} alt="Recommended brand hero" className="h-16 w-28 object-cover border border-[#D3CFC0]" />
              <button onClick={() => apply(recommended)} disabled={busy} data-testid="hero-use-recommended"
                className="inline-flex items-center gap-2 bg-[#2D2D2D] text-[#F3F1EC] font-head uppercase tracking-wide text-sm px-4 py-2.5 hover:bg-[#1E1E1E] transition-colors disabled:opacity-60">
                {busy ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
                Use this photo
              </button>
            </div>
          </div>

          <div className="mt-8">
            <label className="kicker text-[#6E6E6E]" htmlFor="hero-url">Or paste a custom image URL</label>
            <div className="mt-2 flex flex-col sm:flex-row gap-3">
              <input id="hero-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)}
                data-testid="hero-url-input" placeholder="https://…"
                className="flex-1 border-2 border-[#D3CFC0] bg-white px-3 py-2.5 text-sm text-[#1E1E1E] focus:border-[#2D2D2D] outline-none" />
              <ImageUpload api={api} testid="hero-upload" label="Upload" onUploaded={(m) => setUrl(m.url)} />
              <button onClick={() => apply(url)} disabled={busy || !url} data-testid="hero-apply-url"
                className="bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-5 py-2.5 hover:bg-[#D6922C] transition-colors disabled:opacity-60">
                Set as hero
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
